import {
    SettingType,
    CodeBlock,
    BlockType,
    ValidationMessage,
    ValidationMessageType,
    SettingState,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';
import * as simulator from './simulator';


const TS_MAX = 2147483647;

export default class Setting extends BaseFeature {
    constructor() {
        super();
        this.name = 'Setting';
        this.id = 'stg_setting';
        this.singleInstance = false;
        this.settings = this.settings.concat([
            {
                id: 'GENERAL',
                name: 'General',
                settingType: SettingType.GROUP,
                default: '',
                required: true,
            },
            {
                id: 'ADVANCED',
                name: 'Advanced',
                settingType: SettingType.GROUP,
                default: '',
                required: true,
            },
            {
                id: 'SETTINGID',
                name: 'Name',
                settingType: SettingType.NAME,
                dataType: TibboDataType.STRING,
                required: true,
                min: 2,
                max: 32,
                group: 'GENERAL',
                default: 'stg',
            },
            {
                id: 'NAME',
                name: 'Description',
                settingType: SettingType.STRING,
                required: true,
                group: 'GENERAL',
                default: 'Setting Display Name',
            },
            {
                id: 'STORAGE',
                name: 'Storage Type',
                settingType: SettingType.HIDDEN,
                required: true,
                group: 'GENERAL',
                default: 'E',
                options: [
                    {
                        name: 'Non-volatile(EEPROM/Flash',
                        value: 'E',
                    },
                    {
                        name: 'Volatile(RAM)',
                        value: 'R',
                    },
                ],
            },
            {
                id: 'SETTINGTYPE',
                name: 'Setting Type',
                settingType: SettingType.OPTION,
                required: true,
                group: 'GENERAL',
                default: 'S',
                options: [
                    {
                        name: 'String(text)',
                        value: 'string',
                    },
                    {
                        name: 'Value(number)',
                        value: 'float',
                    },
                    {
                        name: 'Date Time',
                        value: 'datetime',
                    },
                    {
                        name: 'Time',
                        value: 'time',
                    },
                ],
            },
            {
                id: 'DATATYPE',
                name: 'Data Type',
                settingType: SettingType.HIDDEN,
                required: true,
                group: 'GENERAL',
                default: 'S',
                options: [
                    {
                        name: 'Byte',
                        value: 'B',
                    },
                    {
                        name: 'Word',
                        value: 'W',
                    },
                    {
                        name: 'String',
                        value: 'S',
                    },
                    {
                        name: 'Float',
                        value: 'F',
                    },
                    {
                        name: 'DateTime',
                        value: 'T',
                    },
                    {
                        name: 'Time',
                        value: 'M',
                    },
                ],
            },
            {
                id: 'MIN',
                name: 'Minimum',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'GENERAL',
                default: '',
            },
            {
                id: 'MAX',
                name: 'Maximum',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'GENERAL',
                default: '',
            },
            {
                id: 'INITIALIZE',
                name: 'Initialization Mode',
                settingType: SettingType.HIDDEN,
                required: true,
                group: 'GENERAL',
                default: 'A',
                options: [
                    {
                        name: 'Initialize Always',
                        value: 'A',
                    },
                    {
                        name: 'Initialize When Invalid',
                        value: 'I',
                    },
                ],
            },
            {
                id: 'DEFAULT',
                name: 'Default Value',
                settingType: SettingType.STRING,
                required: true,
                group: 'GENERAL',
                default: '',
            },
        ]);
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const ID = this.getID();
        const SETTINGTYPE = this.getSettingValue('SETTINGTYPE');
        const P1 = this.getSettingValue('MIN');
        const P2 = this.getSettingValue('MAX');
        const P1_NUM = Number(P1);
        const P2_NUM = Number(P2);
        const DEFAULT = super.getSettingValue('DEFAULT');
        const DATATYPE = this.getSettingValue('DATATYPE');
        if (SETTINGTYPE === 'string') {
            if (P2_NUM > 240) {
                errors.push({
                    message: 'String length must be less than 240',
                    messageType: ValidationMessageType.ERROR,
                    featureId: ID,
                    settingId: 'MAX',
                });
            }
            if (P1_NUM < 0) {
                errors.push({
                    message: 'String length must be greater than 0',
                    messageType: ValidationMessageType.ERROR,
                    featureId: ID,
                    settingId: 'MIN',
                });
            }
            if (P2 !== '' && DEFAULT.length > P2_NUM) {
                errors.push({
                    message: 'Default string length must be less than Maximum Parameter',
                    messageType: ValidationMessageType.ERROR,
                    featureId: ID,
                    settingId: 'DEFAULT',
                });
            }
            if (P1 !== '' && DEFAULT.length < P1_NUM) {
                errors.push({
                    message: 'Default string length must be greater than Minimum Parameter',
                    messageType: ValidationMessageType.ERROR,
                    featureId: ID,
                    settingId: 'DEFAULT',
                });
            }
            if (DATATYPE !== 'S') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: ID,
                    settingId: 'DATATYPE',
                    data: 'S',
                });
            }
        }
        if (SETTINGTYPE === 'float') {
            if (DATATYPE !== 'F') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: ID,
                    settingId: 'DATATYPE',
                    data: 'F',
                });
            }
            if (Number.isNaN(DEFAULT) && this.getSettingValue('SETTINGID').trim().length === 0) {
                errors.push({
                    message: 'Default value must be a number',
                    messageType: ValidationMessageType.ERROR,
                    featureId: ID,
                    settingId: 'DEFAULT',
                });
            } else {
                if (P1 !== '' && Number(DEFAULT) < P1_NUM) {
                    errors.push({
                        message: 'Default value must be greater than Minimum Parameter',
                        messageType: ValidationMessageType.ERROR,
                        featureId: ID,
                        settingId: 'DEFAULT',
                    });
                }
                if (P2 !== '' && Number(DEFAULT) > P2_NUM) {
                    errors.push({
                        message: 'Default value must be less than Maximum Parameter',
                        messageType: ValidationMessageType.ERROR,
                        featureId: ID,
                        settingId: 'DEFAULT',
                    });
                }
            }
        }
        if (SETTINGTYPE === 'datetime') {
            if (DATATYPE !== 'T') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: ID,
                    settingId: 'DATATYPE',
                    data: 'T',
                });
            }
        }
        if (SETTINGTYPE === 'time') {
            if (DATATYPE !== 'M') {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: ID,
                    settingId: 'DATATYPE',
                    data: 'M',
                });
            }
        }

        if (this.getSettingValue('SETTINGID').trim().length === 0) {
            errors.push({ message: 'No ID for setting', messageType: ValidationMessageType.ERROR });
        }

        if (P2 !== '' && P2_NUM < P1_NUM) {
            errors.push({
                message: 'Maximum Parameter must be greater than Minimum Parameter',
                messageType: ValidationMessageType.ERROR,
                featureId: ID,
                settingId: 'MAX',
            });
        }

        const globalVars = this.project.getFeature('global_vars').getSettingValue('AUTOVARS');
        const linkedVariables: BaseFeature[] = [];
        for (let i = 0; i < globalVars.length; i++) {
            const varItem = globalVars[i];
            if (varItem.getSettingValue('FEATURE') === ID) {
                linkedVariables.push(varItem);
            }
        }
        if (linkedVariables.length > 1) {
            // remove the second one
            errors.push({
                featureId: linkedVariables[1].getID(),
                settingId: undefined,
                message: 'Remove duplicate variable',
                messageType: ValidationMessageType.REMOVE,
            });
        }

        return errors;
    }

    getState(settingId: string): SettingState {
        const settings = this.project.getRequiredSettings();
        if (settings.includes(this.getSettingValue('SETTINGID'))) {
            return SettingState.DISABLED;
        }
        if (settingId !== 'EDITABLE' && this.getSettingValue('EDITABLE') === '0') {
            if (this.getSetting(settingId)?.group === 'ADVANCED') {
                return SettingState.HIDDEN;
            }

            return SettingState.ENABLED;
        }

        if (settingId === 'OPTIONS') {
            if (this.getSettingValue('CONTROL') === 'STATIC') {
                return SettingState.ENABLED;
            }

            return SettingState.HIDDEN;
        }
        if (settingId === 'DATATYPE') {
            return SettingState.DISABLED;
        }
        const SETTINGTYPE = this.getSettingValue('SETTINGTYPE');
        if (SETTINGTYPE === 'datetime' || SETTINGTYPE === 'time') {
            if (['MIN', 'MAX', 'DEFAULT'].includes(settingId)) {
                return SettingState.HIDDEN;
            }
        }
        if (settingId === 'DEFAULT'
            || settingId === 'MIN'
            || settingId === 'MAX'
            || settingId === 'SETTINGTYPE') {
            for (let i = 0; i < this.project.features.length; i++) {
                const feature = this.project.features[i];
                if (feature.id === 'linked_setting') {
                    if (feature.getSettingValue('LINKEDSETTING') === this.getSettingValue('ID')) {
                        return SettingState.DISABLED;
                    }
                }
            }
        }
        return SettingState.ENABLED;
    }

    getSettingName(settingId: string): string {
        const DATATYPE = this.getSettingValue('DATATYPE');
        if (settingId === 'MIN' && DATATYPE === 'S') {
            return 'Minimum Length';
        }
        if (settingId === 'MAX' && DATATYPE === 'S') {
            return 'Maximum Length';
        }
        return super.getSettingName(settingId);
    }

    getDependencies() {
        const dependencies = [
            'stg',
        ];
        return dependencies;
    }

    initSettings(project?: any, parentFeature?: any): void {
        super.initSettings(project, parentFeature, 'SETTINGID');
    }

    getSettingValue(settingId: string, getStoredValue?: boolean) {
        if (settingId === 'MIN' || settingId === 'MAX') {
            const SETTINGTYPE = this.getSettingValue('SETTINGTYPE');
            if (SETTINGTYPE === 'datetime' || SETTINGTYPE === 'time') {
                return this.getDefault(settingId);
            }
        }
        return super.getSettingValue(settingId, getStoredValue);
    }

    getDefault(settingId: string) {
        const SettingType = this.getSettingValue('SETTINGTYPE');
        if (settingId === 'MIN') {
            if (SettingType === 'float') {
                return '-2147483647';
            }
            return '0';
        }
        if (settingId !== 'MAX') {
            return super.getDefault(settingId);
        }
        switch (SettingType) {
            case 'string':
                return '240';
            case 'datetime':
                return TS_MAX.toString();
            case 'time':
                return '86400';
            case 'float':
                return '2147483647';
            default:
                return super.getDefault(settingId);
        }
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'tios':
                return tios.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return [];
        }
    }
}
